import { apiClient } from '@/shared/api';
import type {
  BlockOrderUpdateRequest,
  ExpertReportBlockRuleResponse,
  ExpertReportBlocksResponse,
  ExpertReportCreateRequest,
  ExpertReportCreateResponse,
  ExpertReportEvaluationResponse,
  ExpertReportFeedbackFilters,
  ExpertReportFeedbackListResponse,
  ExpertReportListResponse,
  ExpertReportResponse,
  ExpertReportUpdateRequest,
  ManualCommentUpdateRequest,
  ManualRatingUpdateRequest,
  VersionResponse,
  ExpertReportVersionResponse,
} from './types';

// GET /expert-report - list expert reports with pagination
export const fetchExpertReports = async (params?: { limit?: number; offset?: number; isDeleted?: boolean }) => {
  const { data } = await apiClient.get<ExpertReportListResponse>('/expert-report', { params });
  return data;
};

// GET /expert-report/{id} - get expert report by id
export const fetchExpertReportById = async (id: number | string) => {
  const { data } = await apiClient.get<ExpertReportResponse>(`/expert-report/${id}`);
  return data;
};

// POST /expert-report - create expert report
export const createExpertReport = async (body: ExpertReportCreateRequest) => {
  const { data } = await apiClient.post<ExpertReportCreateResponse>('/expert-report', body);
  return data;
};

// PUT /expert-report/{id} - update expert report
export const updateExpertReport = async (params: { id: number; body: ExpertReportUpdateRequest }) => {
  const { data } = await apiClient.put<ExpertReportResponse>(`/expert-report/${params.id}`, params.body);
  return data;
};

// PUT /expert-report/{id}/blocks-order - лёгкая перестановка: только позиции и родители существующих блоков
export const reorderExpertReportBlocks = async (params: { id: number; body: BlockOrderUpdateRequest }) => {
  const { data } = await apiClient.put<ExpertReportResponse>(`/expert-report/${params.id}/blocks-order`, params.body);
  return data;
};

// DELETE /expert-report/{id} - delete expert report (soft delete)
export const deleteExpertReport = async (id: number) => {
  const { data } = await apiClient.delete(`/expert-report/${id}`);
  return data;
};

// GET /expert-report/{id}/version - list versions for an expert report
export const fetchExpertReportVersions = async (id: number) => {
  const { data } = await apiClient.get<VersionResponse[]>(`/expert-report/${id}/version`);
  return data;
};

// GET /expert-report/version/{id} - get state for a specific version
export const fetchExpertReportVersionState = async (id: number) => {
  const { data } = await apiClient.get<ExpertReportVersionResponse>(`/expert-report/version/${id}`);
  return data;
};

// GET /conversation/{conversationId}/expert-report/{expertReportId} - get evaluation results
export const fetchExpertReportEvaluation = async (
  conversationId: number,
  expertReportId: number,
): Promise<ExpertReportEvaluationResponse> => {
  const { data } = await apiClient.get<ExpertReportEvaluationResponse>(
    `/conversation/${conversationId}/expert-report/${expertReportId}`,
  );
  return data;
};

// PATCH /conversation/{conversationId}/expert-report/{expertReportId}/block/{blockId}/rating - сохранение балла блока, выставленного пользователем
export const updateExpertReportBlockManualRating = async (params: {
  conversationId: number;
  expertReportId: number;
  blockId: number;
  manualRating: number | null;
}): Promise<ExpertReportEvaluationResponse> => {
  const { conversationId, expertReportId, blockId, manualRating } = params;
  const body: ManualRatingUpdateRequest = { manualRating };
  const { data } = await apiClient.patch<ExpertReportEvaluationResponse>(
    `/conversation/${conversationId}/expert-report/${expertReportId}/block/${blockId}/rating`,
    body,
  );
  return data;
};

// PATCH /conversation/{conversationId}/expert-report/{expertReportId}/rule/{ruleId}/comment - сохранение изменённого комментария правила
export const updateExpertReportRuleManualComment = async (params: {
  conversationId: number;
  expertReportId: number;
  ruleId: number;
  manualComment: string | null;
}): Promise<ExpertReportEvaluationResponse> => {
  const { conversationId, expertReportId, ruleId, manualComment } = params;
  const body: ManualCommentUpdateRequest = { manualComment };
  const { data } = await apiClient.patch<ExpertReportEvaluationResponse>(
    `/conversation/${conversationId}/expert-report/${expertReportId}/rule/${ruleId}/comment`,
    body,
  );
  return data;
};

// GET /conversation/{conversationId}/expert-report/{expertReportId}/download - скачивание отчёта эксперта
export const downloadExpertReport = async (
  conversationId: number,
  expertReportId: number,
  format: 'docx' | 'pdf' | 'xlsx',
): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(
    `/conversation/${conversationId}/expert-report/${expertReportId}/download`,
    {
      params: { format },
      responseType: 'blob',
    },
  );
  return data;
};

// GET /expert-report/feedback - список правок в результатах заключений, свежие первыми
export const fetchExpertReportFeedback = async (
  params?: ExpertReportFeedbackFilters,
): Promise<ExpertReportFeedbackListResponse> => {
  const { data } = await apiClient.get<ExpertReportFeedbackListResponse>('/expert-report/feedback', {
    params,
  });
  return data;
};

// GET /expert-report/block-rule/{ruleId} - get block rule details
export const fetchExpertReportBlockRule = async (ruleId: number): Promise<ExpertReportBlockRuleResponse> => {
  const { data } = await apiClient.get<ExpertReportBlockRuleResponse>(`/expert-report/block-rule/${ruleId}`);
  return data;
};

// GET /expert-report/{id}/blocks - получить дерево блоков отчёта (без правил, только isDeleted=false)
export const fetchExpertReportBlocks = async (id: number): Promise<ExpertReportBlocksResponse> => {
  const { data } = await apiClient.get<ExpertReportBlocksResponse>(`/expert-report/${id}/blocks`);
  return data;
};
